import React, { useRef } from 'react';

const ARROW_BUTTON =
  'shrink-0 h-11 w-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-xl font-extrabold text-blue-600 hover:bg-gray-50';

/**
 * The Action Songs screen's strip of song tiles (picture, "n. Title") with left/right arrows.
 * `songs` are the row's lessons; a lesson has no picture of its own, so every tile shows `emoji`
 * (the row's sidebar icon). A title that already carries its number ("Action Song 3") isn't
 * numbered again.
 */
export default function SongTabsBar({ songs, activeSongId, onSelectSong, emoji = '🎵' }) {
  const scrollRef = useRef(null);

  const scrollBy = (amount) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="flex items-center gap-3 mb-6 bg-white/70 rounded-2xl border border-gray-100 p-4">
      <button onClick={() => scrollBy(-320)} aria-label="Scroll songs left" className={ARROW_BUTTON}>
        ←
      </button>

      {/* pb-3 leaves room for the active tab's pointer, which would otherwise be clipped by the scroller. */}
      <div
        ref={scrollRef}
        className="flex-1 flex items-start gap-3 overflow-x-auto scroll-smooth pt-1 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {songs.map((song, index) => {
          const isActive = song.id === activeSongId;
          return (
            <button
              key={song.id}
              onClick={() => onSelectSong(song.id)}
              aria-current={isActive ? 'true' : undefined}
              className={`relative flex-1 min-w-[150px] max-w-[220px] min-h-[112px] flex flex-col items-center justify-center gap-1.5 rounded-2xl px-3 py-3 text-center font-extrabold transition-colors ${
                isActive
                  ? 'bg-amber-50 border-2 border-orange-400 text-slate-900 shadow-sm'
                  : 'bg-indigo-50 border-2 border-transparent text-slate-700 hover:border-indigo-200'
              }`}
            >
              <span className="text-4xl leading-none" aria-hidden="true">
                {emoji}
              </span>
              <span className="text-sm leading-tight break-words max-w-full">
                {/\d/.test(song.title) ? song.title : `${index + 1}. ${song.title}`}
              </span>
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute left-1/2 top-full -translate-x-1/2 h-0 w-0 border-l-[9px] border-r-[9px] border-t-[10px] border-l-transparent border-r-transparent border-t-orange-400"
                />
              )}
            </button>
          );
        })}
      </div>

      <button onClick={() => scrollBy(320)} aria-label="Scroll songs right" className={ARROW_BUTTON}>
        →
      </button>
    </div>
  );
}
